import { Navigate, Route } from 'react-router-dom';

import {
  AdminComplianceArea,
  AdminCompliancePage,
  AdminNumberCheckPage,
  AdminNumberChecksArea,
  AdminNumberChecksPage,
  AdminConcernCriteriaPage,
  AdminEscalationLevelsPage,
  AdminStaffFieldFormsPage,
  AdminNoteInitialsPage,
  AdminStaffFieldsPage,
  AdminCyclesPage,
  DdCallsPage,
  DdTemplatesPage,
  AdminExportLayoutPage,
  AdminExportLayoutsArea,
  AdminExportLayoutsPage,
  AdminImportRecord,
  AdminImportsArea,
  AdminImportsPage,
  AdminImportThresholdsPage,
  AdminListsPage,
  AdminRecycleBinPage,
  AdminReinstatementPage,
  MetricsUpdatesPage,
  AdminReportRecord,
  AdminReportsArea,
  AdminReportsPage,
  AdminReportFieldsPage,
  AdminMyTasksArea,
  AdminMyListTaskPage,
  AdminMyMeetingTaskPage,
  AdminMyTasksPage,
  AdminOptionListsPage,
  AdminPeopleArea,
  AdminPeoplePage,
  AdminPersonHubPage,
  AdminRosterCardPage,
  AdminTeamsArea,
  AdminTeamsPage,
  AdminFirmsArea,
  AdminFirmsPage,
  AdminQuestionsPage,
  AdminRankingEditionsPage,
  AdminRankingRecord,
  AdminRankingsArea,
  AdminRefreshRunPage,
  AdminRefreshRunsArea,
  AdminRefreshRunsPage,
  AdminRefreshSchedulePage,
  AdminRegistrationsPage,
  AdminResponseAnswersPage,
  AdminResponseFlagsPage,
  AdminResponseNotesPage,
  AdminResponseTimelinePage,
  AdminResponseViewPage,
  AdminResponsesArea,
  AdminResponsesPage,
  AdminSecAdvFirmPage,
  AdminSearchResultsPage,
  AdminSecAdvFirmsPage,
  AdminSecAdvHome,
  AdminSecAdvStatusPage,
  AdminTeamFirmRoutesPage,
  AdminTeamFirmsRouteRedirect,
  AdminUserDetailPage,
  AdminUsersArea,
  AdminRolePage,
  AdminRolesArea,
  AdminRolesPage,
  AdminUsersPage,
  AdminVersionEditorPage,
  AdminVersionPreviewPage,
  AdminVersionsPage,
  AdminWorkItemPage,
  RankingHistoryTab,
  RankingImportTab,
  RankingFinalistsTab,
  RankingPlacementsTab,
  RankingExportTab,
  RankingSetupTab,
  RankingThresholdTab,
  ReportDefinitionTab,
  ReportPreviewTab,
  ReportRunsTab,
  TEAM_PATHS,
  TEAM_ROOT
} from '@/domains/admin';
import { demoTeamRoutes } from '@/demo';
import { AdminRoute, AuthSplash, useAuth, useCan } from '@/domains/auth';
import {
  ContentAccessPage,
  ContentArea,
  ContentEventEditorPage,
  ContentEventsPage,
  ContentMediaPage,
  ContentNewsPage,
  ContentOverviewPage,
  ContentPostEditorPage,
  ContentSitePagesPage,
  ContentTeamPage
} from '@/domains/content';
import { TeamBoundary } from '../boundary';
import { teamNavFor } from './nav';
import { TeamHome } from './team-home';
import { TeamMain, TeamShell } from './team-shell';

/**
 * The Team Portal's routes, `/team/*` (the two-portals design, §4 and §6) — its own module, beside
 * the Advisor Portal's and sharing nothing with it but `ProtectedRoute`.
 *
 * **The zone's element is its guard.** A Team Portal account (`kind` `staff`) gets the shell,
 * whatever roles it holds; an advisor gets the boundary page at the address they opened, with none
 * of the shell drawn. Inside, each section is guarded by the permission survey-api's policy for
 * its reads names (SR-228; the role-model UI design's §4):
 *
 * - responses, people, registrations, team firms, the compliance queues and My tasks —
 *   `responses.browse`, which survey-api holds the whole participant surface and the work-item
 *   queue to;
 * - the SEC ADV register — `registry.browse`, a guard of its own;
 * - rankings — `internal_fields.read`, the import and the edits inside `rankings.manage`'s;
 * - Setup's sections each their own — the survey builder `definitions.author`, cycles
 *   `cycles.manage`, concern criteria `compliance.configure`, staff fields
 *   `internal_fields.author`, the BrokerCheck refresh `integrations.configure`, users
 *   `accounts.browse`, roles `roles.manage` — and no narrower audience than that permission
 *   (the owner's R3: staff holding `definitions.author` see the builder). `/team/setup`
 *   itself leads to the first Setup entry the rail draws for the account, or home where it draws
 *   none.
 *
 * **A list and its records are one route element** — People, Responses, the compliance queues, My
 * tasks, the register's firms, Users, Roles (`AdminRolesArea`), the rankings' editions and the BrokerCheck refresh's runs
 * are each an area (`Admin…Area`) drawn in the design system's split view: the list is the
 * page until a record is opened, and then the record is the page's one `main` with the list beside
 * it. Every other screen is wrapped in `TeamMain`, the `main` the shell no longer draws.
 *
 * A caller a guard refuses is sent to `/`, which lands them on the Team Portal's home. All of it
 * decides what renders; the access control is survey-api's.
 */
export function teamRoutes() {
  return (
    <Route path={TEAM_ROOT} element={<TeamZone />}>
      {/* SR-231's rankings: a survey's editions, a ranking opened beside them — its placements, the
          scored-result import and its history as routed tabs. `internal_fields.read`, the policy
          survey-api reads them under; the import and the edits are `rankings.manage`, inside. */}
      <Route path="rankings" element={<AdminRoute permission="internal_fields.read" />}>
        <Route element={<AdminRankingsArea />}>
          <Route index element={<AdminRankingEditionsPage />} />
          <Route path=":survey/:list/:year" element={<AdminRankingRecord />}>
            <Route index element={<RankingPlacementsTab />} />
            <Route path="export" element={<RankingExportTab />} />
            <Route path="import" element={<RankingImportTab />} />
            <Route path="finalists" element={<RankingFinalistsTab />} />
            <Route path="history" element={<RankingHistoryTab />} />
            <Route path="threshold" element={<RankingThresholdTab />} />
            <Route path="setup" element={<RankingSetupTab />} />
            {/* The tabs' former addresses, before the process order of 19 Oct. */}
            <Route path="scoring" element={<Navigate to="../export" relative="path" replace />} />
            <Route
              path="territories"
              element={<Navigate to="../setup" relative="path" replace />}
            />
          </Route>
        </Route>
      </Route>

      <Route element={<AdminRoute permission="responses.browse" />}>
        {/* The four panes of one response are routes, not tab state, so a reviewer's link to
            a timeline survives being pasted to a colleague. The detail is fetched by the
            parent and handed down. */}
        <Route path="responses" element={<AdminResponsesArea />}>
          <Route index element={<AdminResponsesPage />} />
          <Route path=":responseId" element={<AdminResponseViewPage />}>
            <Route index element={<AdminResponseAnswersPage />} />
            <Route path="timeline" element={<AdminResponseTimelinePage />} />
            <Route path="flags" element={<AdminResponseFlagsPage />} />
            <Route path="notes" element={<AdminResponseNotesPage />} />
          </Route>
        </Route>
        {/* People (SR-215): the list's search is the URL's query string, so leaving for a hub
            and coming back restores it. The hub is addressed by participant id, as survey-api
            addresses every read and write on it. */}
        <Route path="people" element={<AdminPeopleArea />}>
          <Route index element={<AdminPeoplePage />} />
          {/* A record page's survey tabs and its Account tab (SR-391) are children of the hub's
              route, so the hub stays mounted — and keeps its read — as a tab is pressed. */}
          <Route path=":participantId" element={<AdminPersonHubPage />}>
            <Route path="surveys/:surveyCode" element={null} />
            <Route path="account" element={null} />
          </Route>
          {/* An unregistered member's card (SR-414), by its roster key, beside the list that
              found it — a static segment, so it ranks above `:participantId`. */}
          <Route path="roster/:rosterKey" element={<AdminRosterCardPage />} />
        </Route>
        {/* Teams and Firms (SR-414, the owner's Q2): a team's and a firm's or org's own list, and
            the same record page beside each at its own address — which a record opened under
            another list's address moves itself to once its hub is read. */}
        <Route path="teams" element={<AdminTeamsArea />}>
          <Route index element={<AdminTeamsPage />} />
          <Route path=":participantId" element={<AdminPersonHubPage area="teams" />}>
            <Route path="surveys/:surveyCode" element={null} />
            <Route path="account" element={null} />
          </Route>
        </Route>
        <Route path="firms" element={<AdminFirmsArea />}>
          <Route index element={<AdminFirmsPage />} />
          <Route path=":participantId" element={<AdminPersonHubPage area="firms" />}>
            <Route path="surveys/:surveyCode" element={null} />
            <Route path="account" element={null} />
          </Route>
        </Route>
        {/* SR-222's work items: the queue's state is the URL's query string, and the item — not
            the person, who can hold several — is the record, by survey-api's id. */}
        <Route path="compliance" element={<AdminComplianceArea />}>
          <Route index element={<AdminCompliancePage />} />
          <Route path=":workItemId" element={<AdminWorkItemPage />} />
        </Route>
        {/* SR-487's number checks: the check is the record, keyed with its participant, whose
            card is where survey-api carries it. */}
        <Route path="number-checks" element={<AdminNumberChecksArea />}>
          <Route index element={<AdminNumberChecksPage />} />
          <Route path=":checkKey" element={<AdminNumberCheckPage />} />
        </Route>
        {/* My tasks: the viewer's work items, in the queue's pane, their Lists rows (SR-424) and
            their DD meetings (SR-501). */}
        <Route path="tasks" element={<AdminMyTasksArea />}>
          <Route index element={<AdminMyTasksPage />} />
          <Route path=":workItemId" element={<AdminWorkItemPage crumb="My tasks" />} />
          <Route path="checks/:checkKey" element={<AdminNumberCheckPage crumb="My tasks" />} />
          <Route path="rows/:rowKey" element={<AdminMyListTaskPage />} />
          <Route path="meetings/:interviewId" element={<AdminMyMeetingTaskPage />} />
        </Route>
      </Route>

      <Route path="setup/users" element={<AdminRoute permission="accounts.browse" />}>
        <Route element={<AdminUsersArea />}>
          <Route index element={<AdminUsersPage />} />
          {/* By address, not by account id — because every write on that page is. */}
          <Route path=":email" element={<AdminUserDetailPage />} />
        </Route>
      </Route>

      {/* Users & roles' Roles tab (SR-228): the Team Portal roles, a role opened beside the list —
          its own guard, since the Users tab is `accounts.browse` and this one `roles.manage`. */}
      <Route path="setup/roles" element={<AdminRoute permission="roles.manage" />}>
        <Route element={<AdminRolesArea />}>
          <Route index element={<AdminRolesPage />} />
          <Route path=":roleId" element={<AdminRolePage />} />
        </Route>
      </Route>

      {/* SR-230's Reports — a survey's report views, a report opened beside them: its Definition,
          Preview and Runs as routed tabs, `/new?survey=` a report not saved yet. `responses.browse`,
          the policy survey-api reads views and runs under; a share is `lists.share`, inside. */}
      <Route path="reports" element={<AdminRoute permission="responses.browse" />}>
        <Route element={<AdminReportsArea />}>
          <Route index element={<AdminReportsPage />} />
          <Route path=":viewId" element={<AdminReportRecord />}>
            <Route index element={<ReportDefinitionTab />} />
            <Route path="preview" element={<ReportPreviewTab />} />
            <Route path="runs" element={<ReportRunsTab />} />
          </Route>
        </Route>
      </Route>

      {/* SR-230's Import — a file checked, applied and reversed, an import opened beside the list,
          `/new` the wizard drawn where the import will be. `firm_exchange.manage`, the policy every
          import route of survey-api's names; a restricted import's rows add
          `internal_fields.restricted`, inside. */}
      <Route path="import" element={<AdminRoute permission="firm_exchange.manage" />}>
        <Route element={<AdminImportsArea />}>
          <Route index element={<AdminImportsPage />} />
          <Route path=":importId" element={<AdminImportRecord />} />
        </Route>
      </Route>

      {/* SR-230's export layouts on Setup — a layout opened beside the list. `firm_exchange.manage`,
          the reads included: survey-api reads layouts under `responses.browse` for the export
          dialog, but this is where they are configured (the BrokerCheck refresh's precedent). */}
      <Route path="setup/export-layouts" element={<AdminRoute permission="firm_exchange.manage" />}>
        <Route element={<AdminExportLayoutsArea />}>
          <Route index element={<AdminExportLayoutsPage />} />
          <Route path=":presetId" element={<AdminExportLayoutPage />} />
        </Route>
      </Route>

      {/* SR-222's scheduled BrokerCheck refresh — its Runs tab, a run opened beside the list. The
          Schedule tab, the area's root, is Setup's below, inside `TeamMain`. */}
      <Route
        path="setup/brokercheck-refresh/runs"
        element={<AdminRoute permission="integrations.configure" />}
      >
        <Route element={<AdminRefreshRunsArea />}>
          <Route index element={<AdminRefreshRunsPage />} />
          <Route path=":runId" element={<AdminRefreshRunPage />} />
        </Route>
      </Route>

      <Route element={<TeamMain />}>
        <Route index element={<TeamHome />} />
        {/* SR-463: the top search's Enter — every list's matches for one search. Each list is
            searched under its own guard, so the page decides what it draws (`searchedScopes`). */}
        <Route path="search" element={<AdminSearchResultsPage />} />

        {/* SR-213's A6 queue; its filter and page are the query string, like the list's. SR-230's
            Lists beside it: the survey and the view are the query string, the grid's pane its own
            (not a split view), so it is one screen under the browse read survey-api asks. SR-470's
            recycle bin likewise: a response opened from it is drawn full width. */}
        <Route element={<AdminRoute permission="responses.browse" />}>
          <Route path="registrations" element={<AdminRegistrationsPage />} />
          <Route path="lists" element={<AdminListsPage />} />
          <Route path="reinstatement" element={<AdminReinstatementPage />} />
          <Route path="recycle-bin" element={<AdminRecycleBinPage />} />
          <Route path="metrics-updates" element={<MetricsUpdatesPage />} />
        </Route>

        {/* SR-314's DD calls: the seeded DD list through Lists, under both reads survey-api asks —
            `responses.browse` (the rows) and `internal_fields.read` (the interview); writes are the
            record page's. The rail names the second, which every role holding it holds with the first. */}
        <Route element={<AdminRoute permission="responses.browse" />}>
          <Route element={<AdminRoute permission="internal_fields.read" />}>
            <Route path="dd-calls" element={<DdCallsPage />} />
          </Route>
        </Route>

        {/* Import's Thresholds tab (SR-230, the owner's Q6): `internal_fields.author`, the policy
            survey-api writes a threshold under — not Import's own, so its guard is its own. A
            static segment, ranked above the import's `:importId`. */}
        <Route element={<AdminRoute permission="internal_fields.author" />}>
          <Route path="import/thresholds" element={<AdminImportThresholdsPage />} />
          {/* SR-462: the fields hidden from the pickers, ranked above the report's `:viewId`. */}
          <Route path="reports/fields" element={<AdminReportFieldsPage />} />
        </Route>

        <Route element={<AdminRoute permission="registry.browse" />}>
          <Route path="sec-adv">
            <Route index element={<AdminSecAdvHome />} />
            <Route path="status" element={<AdminSecAdvStatusPage />} />
            {/* SR-214's register, addressed by firm CRD as survey-api addresses it: permanent where
                the SEC number changes when an exempt reporting adviser registers. A firm's page
                is full width, its tabs the optional segment (SR-423). */}
            <Route path="firms" element={<AdminSecAdvFirmsPage />} />
            <Route path="firms/:firmCrd/:tab?" element={<AdminSecAdvFirmPage />} />
          </Route>
        </Route>

        <Route path="setup">
          <Route index element={<SetupIndex />} />
          {/* Team routing (SR-513): the Firms table — every firm by CRD, Wires or Gating, with its
              other names from the team firm register — under People's guard, because survey-api
              reads it under `responses.browse` and changes it under `responses.annotate`. A
              firm's record is a dialog over the table, its CRD the address's `firm`. */}
          <Route path="team-routing" element={<AdminRoute permission="responses.browse" />}>
            <Route index element={<AdminTeamFirmRoutesPage />} />
            {/* Addresses of the tabs there were: the table, under a route's filter, or at
                "Not matched yet". */}
            <Route path="wires" element={<AdminTeamFirmsRouteRedirect route="wires" />} />
            <Route path="gating" element={<AdminTeamFirmsRouteRedirect route="gating" />} />
            <Route path="register" element={<AdminTeamFirmsRouteRedirect />} />
            <Route path="unmapped" element={<AdminTeamFirmsRouteRedirect notMatched />} />
          </Route>
          <Route path="builder" element={<AdminRoute permission="definitions.author" />}>
            <Route index element={<Navigate to={TEAM_PATHS.questions} replace />} />
            <Route path="questions" element={<AdminQuestionsPage />} />
            <Route path="options" element={<AdminOptionListsPage />} />
            <Route path="versions" element={<AdminVersionsPage />} />
            {/* Its own route rather than a panel on the list: a definition editor is somewhere an
                author stays, and a link to one version is worth pasting. */}
            <Route path="versions/:versionId" element={<AdminVersionEditorPage />} />
            {/* ADM-07. A sibling of the editor: previewing is reading, editing is writing, and the
                cycle it renders against lives in `?cycle=`, so the URL is the whole state. */}
            <Route path="versions/:versionId/preview" element={<AdminVersionPreviewPage />} />
          </Route>
          <Route element={<AdminRoute permission="cycles.manage" />}>
            <Route path="cycles" element={<AdminCyclesPage />} />
          </Route>
          {/* SR-222's concern criteria: survey-api reads them under `responses.browse`, but the
              screen is where they are configured (`compliance.configure`), and the work items
              read none of it through this screen. */}
          <Route element={<AdminRoute permission="compliance.configure" />}>
            <Route path="criteria" element={<AdminConcernCriteriaPage />} />
          </Route>
          {/* SR-410's escalation levels: read under `responses.browse`, saved under
              `compliance.configure` — the page draws its controls off without it. */}
          <Route element={<AdminRoute permission="responses.browse" />}>
            <Route path="escalation-levels" element={<AdminEscalationLevelsPage />} />
          </Route>
          {/* SR-226's staff fields: the catalogue and the per-survey form, both
              `internal_fields.author` — the reads included. */}
          <Route path="staff-fields" element={<AdminRoute permission="internal_fields.author" />}>
            <Route index element={<Navigate to={TEAM_PATHS.staffFieldCatalogue} replace />} />
            <Route path="fields" element={<AdminStaffFieldsPage />} />
            <Route path="forms" element={<AdminStaffFieldFormsPage />} />
            {/* SR-494's note initials register — who a notes log's initials are, by period. */}
            <Route path="initials" element={<AdminNoteInitialsPage />} />
          </Route>
          {/* SR-324's interview templates: read under `internal_fields.read`, published under
              `internal_fields.author`, which the page and survey-api both check. */}
          <Route element={<AdminRoute permission="responses.browse" />}>
            <Route element={<AdminRoute permission="internal_fields.read" />}>
              <Route path="dd-templates" element={<DdTemplatesPage />} />
            </Route>
          </Route>
          {/* SR-222's scheduled BrokerCheck refresh — its Schedule tab, the area's root. */}
          <Route element={<AdminRoute permission="integrations.configure" />}>
            <Route path="brokercheck-refresh" element={<AdminRefreshSchedulePage />} />
          </Route>
        </Route>

        {/* The website's content administration (§7.11's WEB-05). One area element, because the
            two screens that are still mocks share one store.

            `content.author` guards the whole section, the rail's entry with it: it is the read
            survey-api asks for on every post route, and the audience it names — whoever writes
            the site — is the audience of the other six screens too. Publishing is the separate
            `content.publish`, checked by survey-api on its own two routes and by the editor's
            publish panel; it is deliberately not a route guard, because an author who may write
            but not publish still belongs on every screen here (WEB-13 AC4). */}
        <Route element={<AdminRoute permission="content.author" />}>
          <Route path="website" element={<ContentArea />}>
            <Route index element={<ContentOverviewPage />} />
            <Route path="news">
              <Route index element={<ContentNewsPage />} />
              {/* Its own address rather than a pane beside the list, for the reason the definition
                  editor has one: writing an article is somewhere an author stays, and a link to a
                  post in progress is worth pasting. */}
              <Route path=":postId" element={<ContentPostEditorPage />} />
            </Route>
            <Route path="team" element={<ContentTeamPage />} />
            <Route path="events">
              <Route index element={<ContentEventsPage />} />
              {/* Its own address, for the reason the post editor has one: an agenda that changes
                  weekly is somewhere an author stays, not a row that expands. */}
              <Route path=":eventId" element={<ContentEventEditorPage />} />
            </Route>
            <Route path="media" element={<ContentMediaPage />} />
            <Route path="pages" element={<ContentSitePagesPage />} />
            <Route path="access" element={<ContentAccessPage />} />
          </Route>
        </Route>

        {/* The rail's mock screens, with the demo on only — each under the guard its entry has. */}
        {demoTeamRoutes()}

        <Route path="*" element={<Navigate to={TEAM_ROOT} replace />} />
      </Route>
    </Route>
  );
}

/** The Team Portal's zone guard: a Team Portal account through — whatever roles it holds, none
 *  included, since Home is its with no section to open — and an advisor refused. */
function TeamZone() {
  const { user } = useAuth();
  if (!user) return <AuthSplash />;
  if (user.kind !== 'staff') return <TeamBoundary />;
  return <TeamShell />;
}

/** `/team/setup`: the first Setup entry the rail draws for this account — Setup has no page of
 *  its own — or home, where the account opens none of it. */
function SetupIndex() {
  const can = useCan();
  const setup = teamNavFor(can).find((group) => group.label === 'Setup');
  const first = setup?.entries.find((entry) => entry.to);
  return <Navigate to={first?.to ?? TEAM_ROOT} replace />;
}
